import { useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  parseTeacherCsv,
  summariseTeacherRows,
  validateTeacherCsvRows,
  type ParsedTeacherRow,
} from '@/lib/csvTeacherImport';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertCircle, CheckCircle2, Download, Info, Loader2, Upload } from 'lucide-react';

type Step = 'upload' | 'preview' | 'importing' | 'done';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableClasses: {
    id: string;
    name: string;
  }[];
  onComplete: () => Promise<void> | void;
}

interface ImportSummary {
  total: number;
  success: number;
  failed: number;
  outcomes: Record<string, number>;
}

const TEMPLATE_HEADERS = 'email,full_name,classes';
const TEMPLATE_EXAMPLE = 'teacher@school.org.uk,Jane Smith,"Class 1A, Class 2B"';

function downloadTemplate() {
  const csv = `${TEMPLATE_HEADERS}\n${TEMPLATE_EXAMPLE}\n`;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'teachers_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function rowStatusBadge(row: ParsedTeacherRow) {
  if (row.imported) {
    return <Badge className="bg-success text-success-foreground text-xs hover:bg-success/90">Processed</Badge>;
  }
  if (row.status === 'error') {
    return <Badge variant="destructive" className="text-xs">Error</Badge>;
  }
  if (row.status === 'duplicate') {
    return <Badge variant="outline" className="text-xs text-warning border-warning/60">Duplicate</Badge>;
  }
  return <Badge variant="secondary" className="text-xs text-success bg-success/10 hover:bg-success/10">Ready</Badge>;
}

export default function TeacherImportDialog({
  open,
  onOpenChange,
  availableClasses,
  onComplete,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [parseError, setParseError] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedTeacherRow[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);

  const summary = useMemo(() => summariseTeacherRows(rows), [rows]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setStep('upload');
      setParseError(null);
      setRows([]);
      setIsImporting(false);
      setImportSummary(null);
    }
    onOpenChange(next);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setParseError('Please upload a .csv file.');
      return;
    }

    setParseError(null);

    try {
      const text = await file.text();
      const { rows: rawRows, parseError } = parseTeacherCsv(text);

      if (parseError) {
        setParseError(parseError);
        return;
      }

      if (rawRows.length === 0) {
        setParseError('No data rows found in the file.');
        return;
      }

      if (rawRows.length > 200) {
        setParseError('Maximum 200 rows per import.');
        return;
      }

      setRows(validateTeacherCsvRows(rawRows, { availableClasses }));
      setStep('preview');
    } catch (err) {
      setParseError((err as Error).message ?? 'Unexpected error reading file.');
    }
  };

  const handleImport = async () => {
    const validRows = rows.filter((row) => row.status === 'valid');
    if (validRows.length === 0) return;

    setStep('importing');
    setIsImporting(true);

    try {
      const { data, error } = await supabase.functions.invoke('bulk-invite-teachers', {
        body: {
          invites: validRows.map((row) => ({
            email: row.email,
            fullName: row.full_name || undefined,
            pendingClassIds: row.resolvedClassIds,
          })),
        },
      });

      if (error || data?.error) {
        throw new Error(error?.message ?? data?.error ?? 'Bulk import failed.');
      }

      const resultByEmail = new Map<string, { outcome?: string; error?: string }>();
      for (const result of data?.results ?? []) {
        if (typeof result?.email === 'string') {
          resultByEmail.set(result.email.toLowerCase(), result);
        }
      }

      const nextRows = rows.map((row) => {
        if (row.status !== 'valid') return row;
        const result = resultByEmail.get(row.email.toLowerCase());
        return {
          ...row,
          imported: !!result,
          outcome: result?.outcome,
          importError: result?.error,
        };
      });

      setRows(nextRows);

      const outcomes: Record<string, number> = {};
      let success = 0;
      let failed = 0;
      for (const row of nextRows) {
        if (row.outcome) {
          outcomes[row.outcome] = (outcomes[row.outcome] ?? 0) + 1;
          success++;
        }
        if (row.importError) {
          failed++;
        }
      }

      setImportSummary({
        total: validRows.length,
        success,
        failed,
        outcomes,
      });

      await onComplete();
      setStep('done');
    } catch (err) {
      setParseError((err as Error).message ?? 'Bulk import failed.');
      setStep('preview');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Import Teachers from CSV</DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-dashed p-6 text-center space-y-3">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Upload className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Upload a teacher CSV</p>
                <p className="text-sm text-muted-foreground">
                  Required columns: <code>email</code>, <code>full_name</code>, <code>classes</code>
                </p>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Button type="button" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="mr-2 h-4 w-4" />
                  Choose CSV
                </Button>
                <Button type="button" variant="outline" onClick={downloadTemplate}>
                  <Download className="mr-2 h-4 w-4" />
                  Download Template
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground space-y-1">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>Unknown class names are treated as warnings only. Those class assignments will be ignored, but the invite row will still remain valid.</p>
              </div>
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>Maximum 200 rows per import.</p>
              </div>
            </div>

            {parseError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{parseError}</p>
              </div>
            )}
          </div>
        )}

        {(step === 'preview' || step === 'done') && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Total rows</p>
                <p className="text-2xl font-semibold">{summary.total}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Ready</p>
                <p className="text-2xl font-semibold">{summary.valid}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Errors</p>
                <p className="text-2xl font-semibold">{summary.error}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Duplicates</p>
                <p className="text-2xl font-semibold">{summary.duplicate}</p>
              </div>
            </div>

            {parseError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{parseError}</p>
              </div>
            )}

            {step === 'done' && importSummary && (
              <div className="rounded-lg border bg-success/5 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  Import complete
                </div>
                <p className="text-sm text-muted-foreground">
                  Processed {importSummary.total} valid row(s): {importSummary.success} successful outcome(s), {importSummary.failed} failed.
                </p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(importSummary.outcomes).map(([outcome, count]) => (
                    <Badge key={outcome} variant="secondary">
                      {outcome}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <ScrollArea className="h-[420px] rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Classes</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.rowNumber}>
                      <TableCell>{row.rowNumber}</TableCell>
                      <TableCell className="font-medium">{row.email || '—'}</TableCell>
                      <TableCell>{row.full_name || '—'}</TableCell>
                      <TableCell className="max-w-[240px]">
                        <div className="space-y-1">
                          <p className="truncate">{row.classes || '—'}</p>
                          {row.resolvedClassIds.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              Matched {row.resolvedClassIds.length} class{row.resolvedClassIds.length === 1 ? '' : 'es'}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{rowStatusBadge(row)}</TableCell>
                      <TableCell className="max-w-[320px]">
                        <div className="space-y-1 text-xs">
                          {row.errors.map((error) => (
                            <p key={error} className="text-destructive">{error}</p>
                          ))}
                          {row.warnings.map((warning) => (
                            <p key={warning} className="text-muted-foreground">{warning}</p>
                          ))}
                          {row.outcome && (
                            <p className="text-muted-foreground">Outcome: {row.outcome}</p>
                          )}
                          {row.importError && (
                            <p className="text-destructive">{row.importError}</p>
                          )}
                          {row.isDuplicateInCsv && row.status === 'duplicate' && (
                            <p className="text-muted-foreground">Duplicate email in file.</p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}

        {step === 'importing' && (
          <div className="py-16 flex flex-col items-center justify-center text-center space-y-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <div>
              <p className="font-medium">Sending invites…</p>
              <p className="text-sm text-muted-foreground">Valid rows are being processed through the bulk invite function.</p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && (
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
          )}
          {step === 'preview' && (
            <>
              <Button type="button" variant="outline" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button type="button" onClick={handleImport} disabled={summary.valid === 0 || isImporting}>
                Import {summary.valid} Valid Row{summary.valid === 1 ? '' : 's'}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button type="button" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
